import React from 'react'
import { Link } from 'react-router-dom'

import Text, { TextProps } from './index'
import Tooltip, { TooltipProps as BaseTooltipProps } from '../Tooltip'

type WithLink = {
  showLink: true
  isLinkDisabled?: boolean
  target?: string
  to: string
}

type WithoutLink = {
  showLink?: false
}

type LinkProps = WithLink | WithoutLink

type WithTooltip = Omit<BaseTooltipProps, 'badge' | 'textAlign' | 'textTransform' | 'variant'> & {
  showTooltip: true
  tooltipBadge?: BaseTooltipProps['badge']
  tooltipTextAlign?: BaseTooltipProps['textAlign']
  tooltipTextTransform?: BaseTooltipProps['textTransform']
  tooltipVariant?: BaseTooltipProps['variant']
}

type WithoutTooltip = {
  showTooltip?: false
}

type TooltipProps = WithTooltip | WithoutTooltip

type Props = LinkProps & TextProps & TooltipProps

const TOOLTIP_PROP_KEYS: Array<keyof BaseTooltipProps> = [
  'inline',
  'badge',
  'position',
  'title',
  'textWidth',
  'textAlign',
  'textTransform',
  'isVisible',
  'contentMaxWidth',
  'zIndex',
  'maxHeight',
  'isScrollable',
  'trigger',
  'open',
  'onOpenChange',
  'variant',
]

const pickTextProps = (rest: Record<string, unknown>, children: TextProps['children']): TextProps => {
  const textProps: Record<string, unknown> = { ...rest }
  for (const key of TOOLTIP_PROP_KEYS) {
    delete textProps[key]
  }
  return { ...textProps, children } as TextProps
}

const DynamicText: React.FC<Props> = ({ children, showLink, showTooltip, ...props }) => {
  const getTextComponent = (textProps: TextProps) => {
    if (showLink) {
      const { isLinkDisabled, target, to } = props as WithLink

      if (isLinkDisabled) {
        return <Text {...textProps}>{children}</Text>
      }

      return (
        <Link target={target} to={to}>
          <Text variant="link" {...textProps}>
            {children}
          </Text>
        </Link>
      )
    }

    return <Text {...textProps}>{children}</Text>
  }

  if (showTooltip) {
    const {
      title,
      tooltipBadge,
      tooltipTextAlign,
      tooltipTextTransform,
      tooltipVariant,
      ...rest
    } = props as WithTooltip

    const textProps = pickTextProps(rest as Record<string, unknown>, children)

    /**
     * Needed to reapply ellipsis style in container level because of tooltip
     */
    if (props.ellipsis) {
      return (
        <div
          style={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          <Tooltip
            {...rest}
            variant={tooltipVariant}
            badge={tooltipBadge}
            textAlign={tooltipTextAlign}
            textTransform={tooltipTextTransform}
            title={title}
          >
            {getTextComponent(textProps)}
          </Tooltip>
        </div>
      )
    }

    return (
      <Tooltip
        {...rest}
        variant={tooltipVariant}
        badge={tooltipBadge}
        textAlign={tooltipTextAlign}
        textTransform={tooltipTextTransform}
        title={title}
      >
        {getTextComponent(textProps)}
      </Tooltip>
    )
  }

  return getTextComponent({ children, ...props })
}

export default DynamicText
